import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import {
  BusinessUnificationAlreadyExistsException,
  BusinessUnificationNeedsMultipleBusinessesException,
  BusinessUnificationNotFromSameCountryException,
} from 'src/app/shared/errors/user-management-service/user-management-service-errors';
import { MatDialogResponse } from 'src/app/shared/models/constants/mat-dialog-response';

@Component({
  selector: 'app-modal',
  templateUrl: './new-business-unification-modal.component.html',
  styleUrls: ['./new-business-unification-modal.component.scss'],
})
/**
 * Modal for new business unification form
 */
export class NewBusinessUnificationModalComponent implements OnInit {
  labels = {
    savingError: 'unexpected-error',
    createSuccess: 'create-new-business-unification-success',
    createError: 'create-new-business-unification-error',
    businessUnificationAlreadyExists:
      'create-new-business-unification-error-already-exists',
    businessUnificationDestinationInvalid:
      'create-new-business-unification-error-destination-invalid',
    businessesNotFromSameCountry:
      'create-new-business-unification-error-not-from-same-country',
    businessUnificationNeedsMultipleBusinesses:
      'create-new-business-unification-error-needs-multiple-businesses',
  };

  loading: boolean = false;

  // Countries
  countries: any[] = [];
  countryMap: any = {};

  // Businesses
  businessList: any[] = [];
  businessMap: any = {};

  // Forms
  selectedCountryId?: number;
  selectedBusinessOrigins: any[] = [];
  originBusinessesIds: number[] = [];
  selectedBusinessDestination: any;

  /**
   * @constructor
   */
  constructor(
    public dialogRef: MatDialogRef<NewBusinessUnificationModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private userService: UserService,
    private snackBarService: SnackBarService,
    private translationService: TranslationService
  ) {}

  /**
   * On Init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);
    this.getCountries();
  }

  /**
   * Get System countries
   */
  getCountries() {
    this.userService.getCountriesByAdmin().subscribe((countries) => {
      countries.sort((a, b) => a.name.localeCompare(b.name));
      this.countries = countries;
      this.countryMap = {};
      for (const country of this.countries) {
        this.countryMap[country.id] = country;
      }
    });
  }

  /**
   * Update the country selected value
   * @param {string[]} selectedValues
   */
  onCountryFilterChanged(selectedValues: string[]) {
    if (selectedValues.length === 1) {
      this.selectedCountryId = parseInt(selectedValues[0]);
      this.getBusinessesBySelectedCountry();
    }
  }

  /**
   * Get System businesses by Country
   */
  getBusinessesBySelectedCountry() {
    this.userService
      .getBusinessesByCountry(this.selectedCountryId!)
      .subscribe((businessList) => {
        this.businessList = businessList.map((business) => ({
          label: business.businessName,
          value: business.businessId,
        }));

        this.selectedBusinessOrigins = [];
        this.selectedBusinessDestination = {};

        for (const business of this.businessList) {
          this.businessMap[business.value] = business;
        }
      });
  }

  /**
   * handles the change of a origin business
   * @param {any} event
   */
  handleChangeOrigin(event: any) {
    const selected = [];
    this.originBusinessesIds = [];

    for (const id of event.value) {
      selected.push(this.businessMap[id]);
      this.originBusinessesIds.push(id);
    }
    this.selectedBusinessOrigins = selected;
  }

  /**
   * handles the change of a destination business
   * @param {any} event
   */
  handleChangeDestination(event: any) {
    this.selectedBusinessDestination = this.businessMap[event.value];
  }

  /**
   * Return a boolean indicating if the form is valid for save
   * @return {boolean}
   */
  isFormValid() {
    return (
      this.selectedBusinessOrigins.length > 0 &&
      this.selectedBusinessDestination.value &&
      this.originBusinessesIds.includes(this.selectedBusinessDestination.value)
    );
  }

  /**
   * Creates a new Business Unification request
   */
  createBusinessUnification() {
    this.loading = true;
    const destinationBusinessId = this.selectedBusinessDestination.value;

    if (!this.originBusinessesIds.includes(destinationBusinessId)) {
      this.snackBarService.error(
        this.labels.businessUnificationDestinationInvalid
      );
      this.loading = false;
      return;
    }

    const data = {
      originBusinessesIds: this.originBusinessesIds,
      destinationBusinessId: destinationBusinessId,
    };

    this.userService.createNewBusinessUnificationRequest(data).subscribe({
      next: () => {
        this.snackBarService.success(this.labels.createSuccess);
        this.dialogRef.close(MatDialogResponse.SUCCESS);
        this.loading = false;
      },
      error: (err) => {
        if (err instanceof BusinessUnificationAlreadyExistsException) {
          this.snackBarService.error(
            this.labels.businessUnificationAlreadyExists
          );
        } else if (
          err instanceof BusinessUnificationNotFromSameCountryException
        ) {
          this.snackBarService.error(this.labels.businessesNotFromSameCountry);
        } else if (
          err instanceof BusinessUnificationNeedsMultipleBusinessesException
        ) {
          this.snackBarService.error(
            this.labels.businessUnificationNeedsMultipleBusinesses
          );
        } else {
          this.snackBarService.error(this.labels.createError);
        }
        this.loading = false;
      },
    });
  }

  /**
   * Action to close the dialog
   */
  onCloseClick() {
    this.dialogRef.close();
  }

  /**
   * Returns current language
   * @return {string}
   */
  getLanguage() {
    return this.translationService.getActiveLang();
  }
}
